import { useEffect, useRef, useState } from "react";
import { Send, Smartphone, Info } from "lucide-react";
import { toast } from "sonner";
import { Rail } from "../components/Rail";
import api, { apiError, timeWIB } from "../lib/api";
import { useWS } from "../hooks/useWS";
import { inputCls } from "../lib/ui";

export default function Simulator() {
  const [phone, setPhone] = useState("6281234567890");
  const [name, setName] = useState("Budi Pelanggan");
  const [text, setText] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [processing, setProcessing] = useState(false);
  const bottomRef = useRef(null);

  const loadThread = async (p) => {
    const { data } = await api.get(`/sim/thread?phone=${encodeURIComponent(p)}`);
    setMsgs(data.messages || []);
  };
  useEffect(() => { loadThread(phone); }, []); // eslint-disable-line

  useWS((event, data) => {
    if (event === "message.created") loadThread(phone);
    if (event === "chat.processing") setProcessing(true);
    if (event === "chat.completed") {
      setProcessing(false);
      loadThread(phone);
    }
    if (event === "token.depleted") toast.error("Token habis — AI tidak membalas. Silakan top up.");
  });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, processing]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post("/sim/incoming", { phone, name, text });
      setText("");
      loadThread(phone);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="flex h-screen bg-ink text-paper">
      <Rail />
      <main className="flex flex-1 flex-col overflow-hidden">
        <header className="border-b border-white/5 bg-ink/80 px-8 py-5 backdrop-blur-md">
          <h1 className="flex items-center gap-2 text-xl font-semibold"><Smartphone size={18} className="text-mint" /> Simulator Pelanggan</h1>
          <p className="text-sm text-muted">Kirim pesan sebagai pelanggan WhatsApp untuk menguji balasan AI (mode mock WAHA)</p>
        </header>
        <div className="flex flex-1 gap-6 overflow-hidden p-8">
          <div className="w-72 shrink-0 space-y-4">
            <div className="rounded-2xl border border-white/5 bg-panel p-5">
              <label className="text-xs text-muted">Nomor pelanggan</label>
              <input data-testid="sim-phone-input" className={`${inputCls} mt-1 font-mono`} value={phone}
                onChange={(e) => setPhone(e.target.value)} onBlur={() => loadThread(phone)} />
              <label className="mt-4 block text-xs text-muted">Nama pelanggan</label>
              <input data-testid="sim-name-input" className={`${inputCls} mt-1`} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="flex gap-2 rounded-2xl border border-mint/15 bg-mint/5 p-4 text-xs text-muted">
              <Info size={14} className="mt-0.5 shrink-0 text-mint" />
              <p>Pesan diproses AI setelah jeda 5 detik (debounce). Balasan muncul otomatis di sini dan di halaman Chats.</p>
            </div>
          </div>

          <div className="flex max-w-md flex-1 flex-col overflow-hidden rounded-2xl border border-white/10">
            <div className="border-b border-white/5 bg-panel2 px-4 py-3">
              <p className="text-sm font-semibold">{name}</p>
              <p className="font-mono text-[11px] text-faint">{phone} — tampilan sisi pelanggan</p>
            </div>
            <div className="chat-wall flex-1 space-y-2 overflow-y-auto p-4" data-testid="sim-thread">
              {msgs.filter((m) => m.role !== "system").map((m) => {
                const fromCustomer = m.role === "user";
                return (
                  <div key={m.id} className={`flex ${fromCustomer ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm shadow-msg ${fromCustomer ? "rounded-br-sm bg-mint-deep ring-1 ring-mint/20" : "rounded-bl-sm bg-panel2"}`}>
                      {m.type === "image" && m.image_url && <img src={m.image_url} alt="" className="mb-1.5 w-full rounded-lg" />}
                      <p className="whitespace-pre-wrap">{m.text}</p>
                      <p className="mt-1 text-right font-mono text-[10px] text-faint">{timeWIB(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              {processing && (
                <div className="flex justify-start">
                  <div className="flex gap-1.5 rounded-xl rounded-bl-sm bg-panel2 px-4 py-3">
                    {[0, 1, 2].map((i) => (
                      <span key={i} className="h-1.5 w-1.5 animate-bounce-dot rounded-full bg-mint" style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="flex items-center gap-2 border-t border-white/5 bg-panel p-3">
              <input data-testid="sim-message-input" className="flex-1 rounded-full bg-ink px-4 py-2.5 text-sm text-paper ring-1 ring-white/10 placeholder:text-faint focus:outline-none focus:ring-mint" placeholder="Ketik sebagai pelanggan…" value={text} onChange={(e) => setText(e.target.value)} />
              <button data-testid="sim-send-btn" className="flex h-10 w-10 items-center justify-center rounded-full bg-mint text-ink transition-transform hover:scale-105">
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
